// Calendar days are 'YYYY-MM-DD' strings in the user's local time; instants are ISO UTC strings.

const pad = (value: number) => String(value).padStart(2, '0');

export function toISODate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Local midnight of a calendar day. */
export function fromISODate(day: string): Date {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, date ?? 1);
}

export const today = (): string => toISODate(new Date());

export function addDays(day: string, amount: number): string {
  const date = fromISODate(day);
  date.setDate(date.getDate() + amount);
  return toISODate(date);
}

export function addMonths(day: string, amount: number): string {
  const date = fromISODate(day);
  return toISODate(new Date(date.getFullYear(), date.getMonth() + amount, 1));
}

export function startOfMonth(day: string): string {
  return `${day.slice(0, 7)}-01`;
}

export function endOfMonth(day: string): string {
  const date = fromISODate(startOfMonth(day));
  return toISODate(new Date(date.getFullYear(), date.getMonth() + 1, 0));
}

/** Monday of the week containing the day (Italian weeks start on Monday). */
export function startOfWeek(day: string): string {
  const offset = (fromISODate(day).getDay() + 6) % 7;
  return addDays(day, -offset);
}

export function daysBetween(from: string, to: string): number {
  return Math.round((fromISODate(to).getTime() - fromISODate(from).getTime()) / 86_400_000);
}

/** The 6×7 grid of a month view, Monday first. */
export function monthGrid(day: string): string[] {
  const first = startOfWeek(startOfMonth(day));
  return Array.from({ length: 42 }, (_, index) => addDays(first, index));
}

export const monthKey = (day: string) => day.slice(0, 7);

const fmt = (options: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('it-IT', options);
const weekdayLong = fmt({ weekday: 'long', day: 'numeric', month: 'long' });
const short = fmt({ weekday: 'short', day: 'numeric', month: 'short' });
const dayMonth = fmt({ day: 'numeric', month: 'short' });
const monthYear = fmt({ month: 'long', year: 'numeric' });
const time = fmt({ hour: '2-digit', minute: '2-digit' });

/** "lunedì 26 ottobre" */
export const formatLongDay = (day: string) => weekdayLong.format(fromISODate(day));
/** "lun 26 ott" */
export const formatShortDay = (day: string) => short.format(fromISODate(day)).replaceAll('.', '');
/** "26 ott" */
export const formatDayMonth = (day: string) =>
  dayMonth.format(fromISODate(day)).replaceAll('.', '');
/** "il 26 ott", "l’8 nov": the day with its article, for "pagato il…", "entro l’…". */
export const theDayMonth = (day: string) => {
  const date = Number(day.slice(8, 10));
  return `${date === 1 || date === 8 || date === 11 ? 'l’' : 'il '}${formatDayMonth(day)}`;
};
/** "ottobre 2026" */
export const formatMonth = (day: string) => monthYear.format(fromISODate(day));
/** "09:30" */
export const formatTime = (instant: string) => time.format(new Date(instant));

export const WEEKDAYS_SHORT = ['lun', 'mar', 'mer', 'gio', 'ven', 'sab', 'dom'];

/** How a due date reads next to a task: "oggi", "domani", "ieri", "lun 3 nov". */
export function formatDue(day: string, reference = today()): string {
  const delta = daysBetween(reference, day);
  if (delta === 0) return 'oggi';
  if (delta === 1) return 'domani';
  if (delta === -1) return 'ieri';
  if (delta > 1 && delta < 7)
    return fromISODate(day).toLocaleDateString('it-IT', { weekday: 'long' });
  return formatShortDay(day);
}

/** Local date and time of an instant, for <input type="date"> / <input type="time">. */
export function splitInstant(instant: string): { day: string; time: string } {
  const date = new Date(instant);
  return { day: toISODate(date), time: `${pad(date.getHours())}:${pad(date.getMinutes())}` };
}

export function joinInstant(day: string, clock: string): string {
  const [hours, minutes] = clock.split(':').map(Number);
  const date = fromISODate(day);
  date.setHours(hours ?? 0, minutes ?? 0, 0, 0);
  return date.toISOString();
}

/** "HH:MM" now: the time a movement entered today starts with. */
export function clockNow(now = new Date()): string {
  return `${pad(now.getHours())}:${pad(now.getMinutes())}`;
}
